import { Bell, BellOff, BellRing, Database, Moon, Settings, Sun, SunMoon, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import OfflineDebugPanel from '@/components/OfflineDebugPanel';
import { PersonalizacaoIaSummaryCard } from '@/components/ia-personalizacao/PersonalizacaoIaSummaryCard';
import { TimbradoSummaryCard } from '@/components/TimbradoSummaryCard';
import { AutosEmNovaAbaCard } from '@/components/AutosEmNovaAbaCard';
import { MeusFeriadosSummaryCard } from '@/components/MeusFeriadosSummaryCard';
import { ConfirmDialog, PageHeader } from '../components/ui';
import { PushDeviceList } from '../components/PushDeviceList';
import { useTheme } from '../context/ThemeProvider';
import { useNotification } from '../context/NotificationProvider';
import { useAuth } from '../hooks/useAuth';
// import { usePasskeySupport } from '../hooks/usePasskeySupport';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { clearOfflineCaches } from '../lib/pwa/clearOfflineData';

const OFFLINE_CACHE_CLEARED_FLAG = 'jurisapp:offline-cache-cleared';

function SettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotification();
  // const { isSupported: isPasskeySupported, isPlatformAuthenticator } = usePasskeySupport();
  const { mode, setMode, isDarkMode } = useTheme();
  const {
    permission,
    isSubscribed,
    isLoading: isPushLoading,
    subscriptions,
    requestPermission,
    unsubscribeCurrentDevice,
    unsubscribe,
  } = usePushNotifications();
  const [clearCachesOpen, setClearCachesOpen] = useState(false);
  const [isClearingCaches, setIsClearingCaches] = useState(false);

  const themeOptions = [
    { value: 'dark', label: 'Escuro', icon: <Moon className="h-5 w-5" /> },
    { value: 'light', label: 'Claro', icon: <Sun className="h-5 w-5" /> },
    { value: 'system', label: 'Sistema', icon: <SunMoon className="h-5 w-5" /> },
  ];

  useEffect(() => {
    if (sessionStorage.getItem(OFFLINE_CACHE_CLEARED_FLAG) !== '1') return;

    sessionStorage.removeItem(OFFLINE_CACHE_CLEARED_FLAG);
    showSuccess('Dados offline apagados deste dispositivo.');
  }, [showSuccess]);

  const handleClearOfflineCaches = async () => {
    if (!user?.id) {
      showError('Não foi possível identificar o usuário atual.');
      return;
    }

    setIsClearingCaches(true);

    try {
      await clearOfflineCaches(user.id);
      sessionStorage.setItem(OFFLINE_CACHE_CLEARED_FLAG, '1');
      setClearCachesOpen(false);
      queryClient.clear();
      window.location.reload();
    } catch {
      showError('Não foi possível apagar os dados offline deste dispositivo.');
      setIsClearingCaches(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        icon={<Settings />}
        title="Configurações"
        subtitle="Gerencie suas preferências e segurança da conta"
      />

        {/* Notificações */}
        <Card className="border bg-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {permission === 'denied' ? (
                  <BellOff className="h-5 w-5 text-destructive" />
                ) : subscriptions.length > 0 ? (
                  <BellRing className="h-5 w-5 text-primary" />
                ) : (
                  <Bell className="h-5 w-5 text-muted-foreground" />
                )}
                <CardTitle className="text-base">Notificações</CardTitle>
              </div>
              {permission === 'denied' ? (
                <Badge variant="destructive">Bloqueadas</Badge>
              ) : permission === 'unsupported' ? (
                <Badge variant="secondary">Indisponível</Badge>
              ) : subscriptions.length > 0 ? (
                <Badge variant="default">Ativas</Badge>
              ) : (
                <Badge variant="outline">Desativadas</Badge>
              )}
            </div>
            <CardDescription>
              Receba alertas de prazos processuais, documentos e petições
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              {permission === 'denied' && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                  <p className="text-sm text-destructive">
                    As notificações foram bloqueadas no navegador. Para reativá-las, acesse as configurações do seu navegador, procure as permissões do site e altere &quot;Notificações&quot; para &quot;Permitir&quot;.
                  </p>
                </div>
              )}

              {permission === 'unsupported' && (
                <p className="text-sm text-muted-foreground">
                  Seu navegador não suporta notificações push.
                </p>
              )}

              {(permission === 'default' || (permission === 'granted' && !isSubscribed)) && (
                <Button onClick={requestPermission} className="w-full sm:w-auto">
                  <Bell className="mr-2 h-4 w-4" />
                  Ativar notificações
                </Button>
              )}

              {permission === 'granted' && isSubscribed && (
                <Button variant="outline" onClick={unsubscribeCurrentDevice} className="w-full sm:w-auto">
                  <BellOff className="mr-2 h-4 w-4" />
                  Desativar neste dispositivo
                </Button>
              )}

              {permission === 'granted' && subscriptions.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium mb-2">Dispositivos registrados</p>
                    {isPushLoading ? (
                      <p className="text-sm text-muted-foreground">Carregando...</p>
                    ) : (
                      <PushDeviceList
                        devices={subscriptions}
                        onRemove={unsubscribe}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* TODO: Chave de acesso (Passkey) — reativar quando Auth0 My Account API
             estiver disponível (plano pago). Passkeys já funcionam via Progressive
             Enrollment no login. Para gerenciamento inline (cadastrar/listar/remover
             do settings), precisa do scope create:me:authentication_methods.
             Ver: https://auth0.com/docs/native-passkeys-api
             SDK: @auth0/myaccount-js */}

        {/* Aparência */}
        <Card className="border bg-card">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-2">
                {isDarkMode ? (
                  <Moon className="h-5 w-5 text-primary" />
                ) : (
                  <Sun className="h-5 w-5 text-primary" />
                )}
                <p className="text-base font-semibold">Aparência</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Escolha como o JurisApp aparece para você. Selecione um tema ou deixe o sistema decidir automaticamente.
              </p>
              {/* No celular, as três opções lado a lado (controle segmentado): empilhadas, ocupavam
                  ~200px para uma escolha de três. O cartão inteiro é o alvo do toque. */}
              <RadioGroup
                value={mode}
                onValueChange={(value) => setMode(value as 'light' | 'dark' | 'system')}
                className="grid grid-cols-3 gap-2 sm:flex sm:flex-row sm:gap-4"
                aria-label="Tema de cores"
              >
                {themeOptions.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`theme-${option.value}`}
                    className={cn(
                      // O rádio some no celular: o foco do teclado aparece no cartão inteiro.
                      'flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 p-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring sm:flex-row sm:gap-3 sm:p-4',
                      mode === option.value
                        ? 'border-primary bg-primary/10'
                        : 'border-transparent hover:bg-muted/50'
                    )}
                  >
                    <RadioGroupItem value={option.value} id={`theme-${option.value}`} className="max-sm:sr-only" />
                    <span className="flex flex-col items-center gap-1 sm:flex-row sm:gap-2">
                      {option.icon}
                      <span>{option.label}</span>
                    </span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Autos: abrir sempre em nova aba (desligado por padrão) */}
        <AutosEmNovaAbaCard />

        {/* Papel timbrado */}
        <TimbradoSummaryCard />

        {/* Personalização da IA (some sem a feature) */}
        <PersonalizacaoIaSummaryCard />

        {/* Feriados e suspensões lançados na conta (contagem de prazos) */}
        <MeusFeriadosSummaryCard />

        {/* Dados offline */}
        <Card className="border bg-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Dados offline</CardTitle>
            </div>
            <CardDescription>
              Use esta ação se algo estiver desatualizado ou inconsistente neste dispositivo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Remove caches locais do app, documentos offline e metadados para baixar tudo novamente do servidor.
                </p>
                <p className="text-xs text-muted-foreground">
                  Ações pendentes salvas offline serão preservadas.
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setClearCachesOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Apagar dados offline
              </Button>
            </div>
          </CardContent>
        </Card>

        <OfflineDebugPanel />

        {/* Perfil */}
        <Card className="border bg-card">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <p className="text-base font-semibold">Perfil</p>
              <Separator />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nome</p>
                  <p>{user?.nome}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p>{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Função</p>
                  <Badge variant="outline">{user?.role}</Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Para alterar sua senha ou configurar autenticação multifator, utilize a tela de login do Auth0.
              </p>
            </div>
          </CardContent>
        </Card>

      <ConfirmDialog
        open={clearCachesOpen}
        title="Apagar dados offline"
        message={(
          <>
            <span>
              Isso remove os dados offline armazenados neste dispositivo e força um novo carregamento do servidor.
            </span>
            <span className="mt-2 block">
              Seus dados da conta não serão apagados e ações pendentes salvas offline serão preservadas.
            </span>
          </>
        )}
        confirmLabel="Apagar dados offline"
        confirmColor="warning"
        loading={isClearingCaches}
        onConfirm={handleClearOfflineCaches}
        onCancel={() => {
          if (!isClearingCaches) setClearCachesOpen(false);
        }}
      />
    </div>
  );
}

export default SettingsPage;
